import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";

import { ApiError, addNote, deleteNote, getPlaybackUrl, listNotes, search } from "./api";
import { getUserEmail, getUserSub, hasAnyGroup, logout } from "./auth";
import AdminGuide from "./AdminGuide";
import AgentHomeView from "./AgentHomeView";
import AgentsPage from "./AgentsPage";
import AuditPage from "./AuditPage";
import Notifications from "./Notifications";
import PendingApprovalView from "./PendingApprovalView";
import RecordingNotes from "./RecordingNotes";
import TroubleshootPage from "./TroubleshootPage";
import UsersPage from "./UsersPage";
import { formatDateTime, formatTime, centralDateBucket, centralBucketDaysAgo } from "./datetime";
import {
  Container,
  PageHeader,
  PageSubtitle,
  PageTitle,
  SectionLabel,
  colors,
  dataGridStyles,
  spacing,
} from "./theme";
import type {
  PlaybackResponse,
  SearchItem,
  SearchParams,
  SearchResponse,
} from "./types";

const todayBucket = () => centralDateBucket();

type TabId = "search" | "agents" | "audit" | "troubleshoot" | "users" | "help";

export default function App() {
  // Routing/permission gate at the very top:
  //   - supervisor/admin    → the full console (Search / Agents / Audit / Troubleshoot [/ Users])
  //   - helper-agents only  → Agent Home (installer + setup steps + self-triage)
  //   - no group            → "pending approval" screen, can sign out
  const isAdmin = hasAnyGroup("admins");
  const isSupervisor = hasAnyGroup("supervisors", "admins");

  if (!isSupervisor) {
    if (hasAnyGroup("helper-agents")) return <AgentHomeView />;
    return <PendingApprovalView />;
  }

  return <AuthorizedApp isAdmin={isAdmin} />;
}

function AuthorizedApp({ isAdmin }: { isAdmin: boolean }) {
  const [activeTab, setActiveTab] = useState<TabId>("search");
  const [filters, setFilters] = useState<SearchParams>({
    agentId: "",
    dateBucket: todayBucket(),
    direction: "",
    ani: "",
    dnis: "",
    csq: "",
  });
  // endDate is UI-only — combined with filters.dateBucket it drives "range mode"
  // (fan-out across days when no agentId, or a single from/to query when there
  // is one). Capped at a 7-day span client-side; the server has no such limit.
  const [endDate, setEndDate] = useState("");
  const [items, setItems] = useState<SearchItem[]>([]);
  const [meta, setMeta] = useState<{ strategy: string; scannedCount: number } | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [playback, setPlayback] = useState<PlaybackResponse | null>(null);
  const [playbackItem, setPlaybackItem] = useState<SearchItem | null>(null);
  // Video element + its current position, so the notes panel can anchor a note
  // to "now" and jump the player to a note's timestamp.
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  // Reason-for-playback flow: when a supervisor clicks Play, we open a small
  // dialog asking for an optional reason. The dialog calls back to play() with
  // the entered text, which gets forwarded to the API and logged in the audit
  // table. Empty reason is allowed.
  const [reasonPromptItem, setReasonPromptItem] = useState<SearchItem | null>(null);
  const [reasonText, setReasonText] = useState("");

  function update<K extends keyof SearchParams>(k: K, v: SearchParams[K]) {
    setFilters((f) => ({ ...f, [k]: v }));
  }

  // True when the user has picked an end date different from the start. In range
  // mode we hide "Load more" — pagination across days isn't worth the complexity
  // for a 7-day cap that maxes out around 7×50 = 350 rows.
  const rangeMode = !!(filters.dateBucket && endDate && endDate !== filters.dateBucket);

  async function runSearch(append: boolean) {
    setLoading(true);
    setError(null);
    try {
      if (rangeMode) {
        const span = daysBetween(filters.dateBucket!, endDate);
        if (span < 0) throw new Error("End date must be on or after the start date.");
        if (span > 6) throw new Error("Date range is limited to 7 days.");

        const base = stripEmpty(filters);
        delete base.dateBucket;

        if (filters.agentId) {
          // Single byAgent query with a SK range across the whole window.
          // startTimeContactId sorts lexicographically by ISO timestamp prefix,
          // so a string from/to bounds the right rows.
          const res = await search({
            ...base,
            from: isoStartOfDay(filters.dateBucket!),
            to: isoEndOfDay(endDate),
          });
          setItems(res.items);
          setMeta({ strategy: res.strategy, scannedCount: res.scannedCount });
          setNextCursor(null);
        } else {
          // No agent anchor — fan out one byDate query per day, merge, sort.
          const days = enumerateDays(filters.dateBucket!, endDate);
          const results = await Promise.all(days.map((d) => search({ ...base, dateBucket: d })));
          const merged = results.flatMap((r) => r.items);
          merged.sort((a, b) => (b.startTime ?? "").localeCompare(a.startTime ?? ""));
          setItems(merged);
          setMeta({
            strategy: `byDate × ${days.length}`,
            scannedCount: results.reduce((s, r) => s + r.scannedCount, 0),
          });
          setNextCursor(null);
        }
        return;
      }

      const params: SearchParams = stripEmpty(filters);
      if (append && nextCursor) params.cursor = nextCursor;
      const res: SearchResponse = await search(params);
      setItems(append ? [...items, ...res.items] : res.items);
      setMeta({ strategy: res.strategy, scannedCount: res.scannedCount });
      setNextCursor(res.nextCursor);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.body}` : String(e));
    } finally {
      setLoading(false);
    }
  }

  function openReasonPrompt(item: SearchItem) {
    setReasonText("");
    setReasonPromptItem(item);
  }

  async function play(item: SearchItem, reason: string) {
    setError(null);
    setReasonPromptItem(null);
    try {
      const pb = await getPlaybackUrl(item.contactId, reason);
      setPlayback(pb);
      setPlaybackItem(item);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.body}` : String(e));
    }
  }

  function closePlayback() {
    setPlayback(null);
    setPlaybackItem(null);
    setCurrentTimeMs(0);
  }

  // Date presets just rewrite the dateBucket field. They don't auto-fire the
  // search — user still hits Search — so they can tweak other filters before
  // submitting.
  function setDatePreset(preset: "today" | "yesterday" | "last7" | "clear") {
    if (preset === "clear") {
      setFilters((f) => ({ ...f, dateBucket: "", from: "", to: "" }));
      setEndDate("");
      return;
    }
    if (preset === "last7") {
      setFilters((f) => ({ ...f, dateBucket: centralBucketDaysAgo(6) }));
      setEndDate(centralDateBucket());
      return;
    }
    const bucket = preset === "yesterday" ? centralBucketDaysAgo(1) : centralDateBucket();
    setFilters((f) => ({ ...f, dateBucket: bucket }));
    setEndDate("");
  }

  return (
    <Container>
      <PageHeader>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack>
            <PageTitle>Agent Screen Recordings</PageTitle>
            <PageSubtitle>
              {activeTab === "users"
                ? "Approve, demote, and disable user accounts."
                : activeTab === "agents"
                  ? "See which agent helpers are online and what they're doing."
                  : activeTab === "audit"
                    ? "Playback audit log — who watched which recording, and when."
                    : activeTab === "troubleshoot"
                      ? "Paste an agent's helper log for an AI triage of what's wrong."
                      : activeTab === "help"
                        ? "How this console works — roles, approvals, search, audit, fleet."
                        : "Find and play back captured agent screen recordings."}
            </PageSubtitle>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon fontSize="small" />}
              href="/admin/downloads/uccx-recorder-helper-apc-installer-latest.exe"
              download
            >
              Installer
            </Button>
            <Notifications
              isAdmin={isAdmin}
              onJumpToTab={(tab) => setActiveTab(tab)}
            />
            {getUserEmail() && (
              <Typography variant="caption" sx={{ color: colors.gray500 }}>
                {getUserEmail()}
              </Typography>
            )}
            <Button size="small" variant="outlined" onClick={logout}>
              Sign out
            </Button>
          </Stack>
        </Stack>

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v as TabId)}
          sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab value="search" label="Search" />
          <Tab value="agents" label="Agents" />
          <Tab value="audit" label="Audit" />
          <Tab value="troubleshoot" label="Troubleshoot" />
          {isAdmin && <Tab value="users" label="Users" />}
          <Tab value="help" label="Help" />
        </Tabs>
      </PageHeader>

      {activeTab === "users" && isAdmin ? <UsersPage /> : activeTab === "agents" ? <AgentsPage /> : activeTab === "audit" ? <AuditPage /> : activeTab === "troubleshoot" ? <TroubleshootPage /> : activeTab === "help" ? <AdminGuide /> : (
        <>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="caption" sx={{ color: colors.gray500, mr: 0.5 }}>
              Quick dates:
            </Typography>
            <Chip
              label="Today"
              size="small"
              onClick={() => setDatePreset("today")}
              color={filters.dateBucket === todayBucket() && !endDate ? "primary" : "default"}
              variant={filters.dateBucket === todayBucket() && !endDate ? "filled" : "outlined"}
              clickable
            />
            <Chip
              label="Yesterday"
              size="small"
              onClick={() => setDatePreset("yesterday")}
              clickable
              variant="outlined"
            />
            <Chip
              label="Last 7 days"
              size="small"
              onClick={() => setDatePreset("last7")}
              clickable
              variant="outlined"
            />
            <Chip
              label="Clear date"
              size="small"
              onClick={() => setDatePreset("clear")}
              clickable
              variant="outlined"
            />
          </Stack>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch(false);
            }}
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: spacing.md,
              alignItems: "end",
            }}
          >
            <TextField
              size="small"
              type="date"
              fullWidth
              label={filters.agentId ? "Date (blank = all)" : "Date"}
              InputLabelProps={{ shrink: true }}
              value={filters.dateBucket ?? ""}
              onChange={(e) => update("dateBucket", e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              fullWidth
              label="End date (range, max 7d)"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <TextField
              size="small"
              label="Agent ID"
              placeholder="agent@domain"
              value={filters.agentId ?? ""}
              onChange={(e) => update("agentId", e.target.value)}
            />
            <TextField
              size="small"
              select
              label="Direction"
              value={filters.direction ?? ""}
              onChange={(e) => update("direction", e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="INBOUND">Inbound</MenuItem>
              <MenuItem value="OUTBOUND">Outbound</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="ANI contains"
              placeholder="+1440..."
              value={filters.ani ?? ""}
              onChange={(e) => update("ani", e.target.value)}
            />
            <TextField
              size="small"
              label="DNIS contains"
              placeholder="+1636..."
              value={filters.dnis ?? ""}
              onChange={(e) => update("dnis", e.target.value)}
            />
            <TextField
              size="small"
              label="Queue contains"
              placeholder="Sales"
              value={filters.csq ?? ""}
              onChange={(e) => update("csq", e.target.value)}
            />
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Searching…" : "Search"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {meta && items.length > 0 && <KpiCards items={items} />}

      {meta && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center" }}>
          <SectionLabel>Strategy</SectionLabel>
          <Chip label={meta.strategy} size="small" sx={{ fontFamily: "monospace" }} />
          <Typography variant="caption" sx={{ color: colors.gray500 }}>
            {items.length} result{items.length === 1 ? "" : "s"}
            {meta.scannedCount !== items.length && ` (scanned ${meta.scannedCount})`}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon fontSize="small" />}
            onClick={() => exportCsv(items)}
            disabled={items.length === 0}
          >
            Export CSV
          </Button>
        </Stack>
      )}

      <DataGrid
        rows={items}
        columns={columns(openReasonPrompt)}
        getRowId={(r) => `${r.agentId}-${r.contactId}`}
        density="compact"
        autoHeight
        disableRowSelectionOnClick
        hideFooter
        sx={dataGridStyles.compact}
        slots={{
          noRowsOverlay: () => (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ py: 4, color: colors.gray500, fontStyle: "italic" }}
            >
              {meta ? "No matches." : "Run a search to see results."}
            </Stack>
          ),
        }}
      />

      {nextCursor && !rangeMode && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button variant="outlined" onClick={() => runSearch(true)} disabled={loading}>
            Load more
          </Button>
        </Box>
      )}

      <Dialog
        open={!!reasonPromptItem}
        onClose={() => setReasonPromptItem(null)}
        maxWidth="sm"
        fullWidth
      >
        {reasonPromptItem && (
          <>
            <DialogTitle>Reason for playback (optional)</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ color: colors.gray500, mb: 2 }}>
                Contact <strong style={{ fontFamily: "monospace" }}>{reasonPromptItem.contactId}</strong> · agent {formatAgent(reasonPromptItem)}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Reason"
                placeholder="QA review, escalation, compliance audit…"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    play(reasonPromptItem, reasonText);
                  }
                }}
              />
              <Typography variant="caption" sx={{ display: "block", color: colors.gray500, mt: 1 }}>
                This is logged in the audit trail. Leave blank to skip.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: "flex-end" }}>
                <Button onClick={() => setReasonPromptItem(null)}>Cancel</Button>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => play(reasonPromptItem, reasonText)}
                >
                  Play
                </Button>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog open={!!playback} onClose={closePlayback} maxWidth="md" fullWidth>
        {playback && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
              <Typography component="strong" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
                {playback.contactId}
              </Typography>
              <Typography component="span" sx={{ color: colors.gray500, fontSize: "0.875rem" }}>
                · {playback.agentId}
              </Typography>
              <IconButton onClick={closePlayback} sx={{ ml: "auto" }} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box
                component="video"
                ref={videoRef}
                src={playback.url}
                controls
                autoPlay
                onTimeUpdate={(e) => setCurrentTimeMs((e.currentTarget as HTMLVideoElement).currentTime * 1000)}
                sx={{ width: "100%", background: "#000", borderRadius: 1 }}
              />
              <Typography variant="caption" sx={{ display: "block", mt: 1, color: colors.gray500 }}>
                Expires {formatTime(playback.expiresAt)}
              </Typography>
              {playbackItem && <CallDetail item={playbackItem} />}
              <RecordingNotes
                onLoad={async () => (await listNotes(playback.contactId)).notes}
                onAdd={(note) => addNote(playback.contactId, note)}
                onDelete={(noteId) => deleteNote(playback.contactId, noteId)}
                viewerSub={getUserSub()}
                canModerate={hasAnyGroup("admins")}
                currentTimeMs={currentTimeMs}
                onSeek={(offsetMs) => {
                  const v = videoRef.current;
                  if (v) {
                    v.currentTime = offsetMs / 1000;
                    void v.play();
                  }
                }}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
        </>
      )}
    </Container>
  );
}

function columns(play: (item: SearchItem) => void): GridColDef<SearchItem>[] {
  return [
    {
      field: "contactId",
      headerName: "Contact",
      width: 130,
      renderCell: (p: GridRenderCellParams<SearchItem, string>) => (
        <Typography sx={{ fontFamily: "monospace", color: colors.primary, fontWeight: 500 }}>
          {p.value}
        </Typography>
      ),
    },
    {
      field: "agent",
      headerName: "Agent",
      flex: 1,
      minWidth: 160,
      valueGetter: (_v, row) => formatAgent(row),
      renderCell: (p: GridRenderCellParams<SearchItem, string>) => (
        <Box title={p.row.agentId}>{p.value}</Box>
      ),
    },
    { field: "csq", headerName: "Queue", width: 110, valueFormatter: (v) => v ?? "—" },
    { field: "ani", headerName: "ANI", width: 140, valueFormatter: (v) => v ?? "—" },
    { field: "dnis", headerName: "DNIS", width: 140, valueFormatter: (v) => v ?? "—" },
    {
      field: "direction",
      headerName: "Direction",
      width: 110,
      renderCell: (p: GridRenderCellParams<SearchItem, string | null>) =>
        p.value ? (
          <Chip
            label={p.value}
            size="small"
            color={p.value === "INBOUND" ? "primary" : "secondary"}
            variant="outlined"
          />
        ) : (
          <span>—</span>
        ),
    },
    {
      field: "startTime",
      headerName: "Start",
      width: 170,
      valueFormatter: (v: string | null) => formatStart(v),
    },
    {
      field: "durationMs",
      headerName: "Duration",
      width: 100,
      valueFormatter: (v: number | null) => formatDuration(v),
    },
    {
      field: "actions",
      headerName: "",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<SearchItem>) => (
        <Button
          size="small"
          startIcon={<PlayArrowIcon />}
          onClick={() => play(p.row)}
        >
          Play
        </Button>
      ),
    },
  ];
}

function CallDetail({ item }: { item: SearchItem }) {
  const rows: [string, string | null][] = [
    ["Agent extension", item.agentExtension],
    ["Direction", item.direction],
    ["Call type", item.callType],
    ["Queue", item.csq],
    ["Outbound classification", item.outboundClassification],
    ["Wrap-up reason", item.wrapUpReason],
    ["Monitors captured", item.monitorCount != null ? String(item.monitorCount) : null],
  ];
  const visible = rows.filter(([, v]) => v && v.length > 0);
  const cv = item.callVariables ?? {};
  const cvEntries = Object.entries(cv).filter(([, v]) => v && v.length > 0);

  if (visible.length === 0 && cvEntries.length === 0) return null;

  const detailRow = (k: string, v: string) => (
    <Stack key={k} direction="row" spacing={2} sx={{ py: 0.5 }}>
      <Typography
        sx={{ minWidth: 200, color: colors.gray500, fontSize: "0.8rem" }}
      >
        {k}
      </Typography>
      <Typography sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{v}</Typography>
    </Stack>
  );

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.gray200}` }}>
      {visible.length > 0 && <Box>{visible.map(([k, v]) => detailRow(k, v!))}</Box>}
      {cvEntries.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <SectionLabel>Call variables</SectionLabel>
          <Box sx={{ mt: 1 }}>{cvEntries.map(([k, v]) => detailRow(k, v))}</Box>
        </Box>
      )}
    </Box>
  );
}

// KPI cards row — computed from the CURRENT search results (not a separate
// API call). Mirrors what the user sees in the table, so "Recordings today"
// reflects whatever the active filter scope is.
function KpiCards({ items }: { items: SearchItem[] }) {
  const total = items.length;
  const uniqueAgents = new Set(items.map((i) => i.agentId)).size;
  const totalMs = items.reduce((acc, i) => acc + (i.durationMs ?? 0), 0);
  const avgMs = total > 0 ? totalMs / total : 0;
  const truncated = items.filter((i) => i.pciTruncated).length;

  return (
    <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
      <KpiCard label="Recordings" value={String(total)} />
      <KpiCard label="Unique agents" value={String(uniqueAgents)} />
      <KpiCard label="Avg duration" value={formatDuration(avgMs)} />
      <KpiCard
        label="Truncated"
        value={String(truncated)}
        accent={truncated > 0 ? "warning" : "neutral"}
      />
    </Stack>
  );
}

function KpiCard({
  label,
  value,
  accent = "neutral",
}: {
  label: string;
  value: string;
  accent?: "neutral" | "warning";
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, minWidth: 140, flex: 1 }}
    >
      <Typography
        variant="caption"
        sx={{
          color: colors.gray500,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
          fontSize: "0.7rem",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: accent === "warning" && value !== "0" ? colors.warning : colors.gray900,
          mt: 0.5,
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
}

// CSV export of the current search results. Browser-side only — no API call.
// Quoted per RFC 4180: any cell containing comma, double-quote, or newline
// gets wrapped in quotes with internal quotes doubled.
function exportCsv(items: SearchItem[]) {
  const headers = [
    "Contact ID",
    "Agent",
    "Email",
    "Agent extension",
    "ANI",
    "DNIS",
    "Direction",
    "Call type",
    "Queue",
    "Start time",
    "Duration (s)",
    "Captured by",
    "Truncated",
    "Recording key",
  ];
  const rows = items.map((it) => [
    it.contactId,
    formatAgent(it),
    it.agentId,
    it.agentExtension ?? "",
    it.ani ?? "",
    it.dnis ?? "",
    it.direction ?? "",
    it.callType ?? "",
    it.csq ?? "",
    it.startTime ?? "",
    it.durationMs != null ? Math.round(it.durationMs / 1000) : "",
    it.captureMethod ?? "",
    it.pciTruncated ? "true" : "",
    it.recordingKey,
  ]);

  const csv = [headers, ...rows]
    .map((r) => r.map(escapeCsvCell).join(","))
    .join("\r\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `recordings-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapeCsvCell(v: string | number): string {
  const s = String(v ?? "");
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function stripEmpty(p: SearchParams): SearchParams {
  const out: SearchParams = {};
  for (const [k, v] of Object.entries(p)) {
    if (v != null && v !== "") (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

function isoStartOfDay(d: string): string {
  return `${d}T00:00:00.000Z`;
}

function isoEndOfDay(d: string): string {
  return `${d}T23:59:59.999Z`;
}

function daysBetween(start: string, end: string): number {
  return Math.round((Date.parse(end) - Date.parse(start)) / 86_400_000);
}

function enumerateDays(start: string, end: string): string[] {
  const out: string[] = [];
  const d = new Date(`${start}T00:00:00Z`);
  const stop = new Date(`${end}T00:00:00Z`);
  while (d <= stop) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

function formatAgent(it: SearchItem): string {
  const last = it.agentLastName?.trim();
  const first = it.agentFirstName?.trim();
  if (last && first) return `${last}, ${first}`;
  if (last || first) return last ?? first ?? it.agentId;
  return it.agentId;
}

function formatStart(iso: string | null): string {
  return formatDateTime(iso);
}

function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}
